import { BUILT_IN_PRESETS } from "./presets";
import type { BuiltInThemeName, CustomThemeInput, ThemeSizeTokens, ThemeThickness, ThemeTokens } from "./types";
import { BUILT_IN_THEME_NAMES } from "./types";

export type { BuiltInThemeName, CustomThemeInput, ThemeSizeTokens, ThemeThickness, ThemeTokens } from "./types";
export { BUILT_IN_THEME_NAMES } from "./types";

const DEFAULT_BUILT_IN_THEME: BuiltInThemeName = "light";

/**
 * One size table: themes differ in colour only. `thin` remains a valid key because stored
 * custom themes still carry it, and it maps to the same sizes as `bold`.
 */
const SIZE: ThemeSizeTokens = {
  borderWidth: 6,
  borderRadius: 6,
  fontWeight: 700,
  fontWeightBold: 700,
  textTransform: "uppercase",
  rtTotalPadding: "10px",
};
export const THEME_SIZES: Record<ThemeThickness, ThemeSizeTokens> = { thin: SIZE, bold: SIZE };

/**
 * The old app's `create_theme()`, ported verbatim from Appendix B: every one of the 28 tokens
 * is a direct reuse of one of the 7 input fields (or a hardcoded white), no computation.
 */
function deriveThemeTokens(input: CustomThemeInput): ThemeTokens {
  return {
    tab_bg: input.inactive_tab,
    tab_active_bg: input.main_tab,
    tab_text: input.main_tab,
    tab_active_text: input.inactive_tab,
    realtime_bg: input.realtime_bg,
    availability_border: input.main_tab,
    realtime_inner_border: input.inactive_tab,
    realtime_small_text: input.content_text,
    realtime_big_text: input.content_text_strong,
    realtime_category_text: input.content_text,
    realtime_total_bg: input.main_tab,
    realtime_total_text: input.realtime_bg,
    prediction_bg: input.prediction_bg,
    prediction_border: input.main_tab,
    prediction_text: input.content_text,
    prediction_gauge_title: input.content_text,
    prediction_gauge_percentage: input.content_text_strong,
    prediction_gauge: input.realtime_bg,
    prediction_gauge_topring: input.content_text_strong,
    realtime_powered_by_gymplus: input.content_text,
    weekdayrow_bg: input.main_tab,
    weekdayrow_border: input.main_tab,
    weekdayrow_text: input.inactive_tab,
    weekdayrow_active_bg: "#FFFFFF",
    weekdayrow_active_text: input.main_tab,
    chart_bg: "#FFFFFF",
    chart_border: input.main_tab,
    chart_powered_by_gymplus: input.content_text,
    thickness: input.thickness,
  };
}

const REQUIRED_CUSTOM_THEME_FIELDS: (keyof CustomThemeInput)[] = [
  "main_tab",
  "inactive_tab",
  "realtime_bg",
  "content_text",
  "content_text_strong",
  "prediction_bg",
];

function isValidCustomThemeInput(value: unknown): value is CustomThemeInput {
  if (value === null || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  const hasAllFields = REQUIRED_CUSTOM_THEME_FIELDS.every((field) => typeof record[field] === "string" && record[field] !== "");
  const hasValidThickness = record.thickness === "thin" || record.thickness === "bold";
  return hasAllFields && hasValidThickness;
}

function resolveBuiltInTheme(name?: string): ThemeTokens {
  const themeName = (BUILT_IN_THEME_NAMES as readonly string[]).includes(name ?? "")
    ? (name as BuiltInThemeName)
    : DEFAULT_BUILT_IN_THEME;
  return BUILT_IN_PRESETS[themeName];
}

/**
 * `customCss` is the JSON string read from RDS's `app_settings.gp_widget.css` (see
 * packages/shared/src/db/repository.ts's `getCustomCss`). When present and well-formed, it's
 * run through the same derivation as the old `create_theme()`. Otherwise falls back to a
 * built-in preset (default 'light' if `builtInThemeName` is omitted or unrecognized) — built-in
 * presets do NOT go through `deriveThemeTokens`, they're their own literal tables (see
 * presets.ts's doc comment for why: the real light/dark data doesn't fit that formula).
 */
export function computeThemeTokens(customCss: string | null | undefined, builtInThemeName?: string): ThemeTokens {
  if (!customCss) return resolveBuiltInTheme(builtInThemeName);

  try {
    const parsed: unknown = JSON.parse(customCss);
    if (isValidCustomThemeInput(parsed)) return deriveThemeTokens(parsed);
    console.error("computeThemeTokens: custom_css JSON is missing required fields, falling back to built-in theme", parsed);
  } catch (error) {
    console.error("computeThemeTokens: custom_css is not valid JSON, falling back to built-in theme", error);
  }

  return resolveBuiltInTheme(builtInThemeName);
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace("#", "");
  const full = normalized.length === 3 ? normalized.split("").map((c) => c + c).join("") : normalized;
  const value = parseInt(full, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

function clampChannel(value: number): number {
  return Math.max(0, Math.min(255, value));
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((channel) => clampChannel(channel).toString(16).padStart(2, "0")).join("").toUpperCase()}`;
}

/**
 * Ported verbatim from Appendix B: `i` starts at 0 (so the first shade equals the base color
 * unchanged) and steps linearly by 15 per channel per step — not perceptually interpolated.
 * Reproduces the old chart's exact stepping rather than "improving" it to a nicer ramp.
 */
export function generateBarColors(hex: string, amount: number): string[] {
  const { r, g, b } = hexToRgb(hex);
  const plus = r < 140 || g < 140 || b < 140;
  const colors: string[] = [];

  for (let i = 0; i < amount; i += 1) {
    const delta = i * 15 * (plus ? 1 : -1);
    colors.push(rgbToHex(r + delta, g + delta, b + delta));
  }

  return colors.reverse();
}
